<template>
  <Dialog 
    :visible="visible" 
    modal 
    :header="'Image Suggestions'" 
    :style="{ width: '90vw', maxWidth: '800px' }"
    :closable="true"
    @hide="handleClose"
    @update:visible="$emit('update:visible', $event)"
  >
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-8"
    >
      <i class="pi pi-spin pi-spinner text-4xl text-gray-400 mb-4" />
      <p class="text-gray-600">
        Loading suggestions...
      </p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!suggestions || suggestions.length === 0"
      class="flex flex-col items-center justify-center py-8"
    >
      <i class="pi pi-image text-6xl text-gray-300 mb-4" />
      <h3 class="text-xl font-semibold text-gray-700 mb-2">
        No similar images found
      </h3>
      <p class="text-gray-500 text-center mb-6">
        We couldn't find any similar images for this asset type.
      </p>
      <Button 
        label="Upload New Image" 
        icon="pi pi-upload" 
        class="p-button-primary"
        @click="handleUploadNew"
      />
    </div>

    <!-- Suggestions Grid -->
    <div
      v-else
      class="space-y-6"
    >
      <!-- Grid of suggestions -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div 
          v-for="(suggestion, index) in suggestions" 
          :key="index"
          class="relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200"
          :class="{
            'border-primary shadow-lg scale-105': selectedSuggestion === index,
            'border-gray-200 hover:border-gray-300': selectedSuggestion !== index
          }"
          @click="selectSuggestion(index)"
        >
          <!-- Image -->
          <div class="aspect-square relative">
            <img 
              :src="suggestion.image_url" 
              :alt="`Suggestion ${index + 1}`"
              class="w-full h-full object-cover"
              @load="handleImageLoad"
              @error="handleImageError"
            >
                        
            <!-- Similarity Score Badge -->
            <div class="absolute top-2 right-2">
              <Badge 
                :value="`${suggestion.similarity_score}%`"
                :class="getSimilarityBadgeClass(suggestion.similarity_score)"
              />
            </div>
                        
            <!-- Source Badge -->
            <div class="absolute top-2 left-2">
              <Badge 
                :value="suggestion.source === 'company_specific' ? 'Company' : 'Shared'"
                :class="suggestion.source === 'company_specific' ? 'bg-green-500' : 'bg-gray-500'"
              />
            </div>
                        
            <!-- Selection Overlay -->
            <div 
              v-if="selectedSuggestion === index"
              class="absolute inset-0 bg-primary bg-opacity-20 flex items-center justify-center"
            >
              <i class="pi pi-check text-white text-2xl" />
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Image Details -->
      <div
        v-if="selectedSuggestion !== null"
        class="bg-gray-50 rounded-lg p-4"
      >
        <h4 class="font-semibold text-gray-800 mb-3">
          Selected Image Details
        </h4>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div v-if="selectedSuggestionData?.cable_specs?.length">
            <span class="font-medium text-gray-600">Length:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.length }}m</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.rating">
            <span class="font-medium text-gray-600">Rating:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.rating }}A</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.phase_type">
            <span class="font-medium text-gray-600">Phase:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.phase_type }}</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.color">
            <span class="font-medium text-gray-600">Color:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.color }}</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.connector_a">
            <span class="font-medium text-gray-600">Connector A:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.connector_a }}</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.connector_b">
            <span class="font-medium text-gray-600">Connector B:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.connector_b }}</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.voltage">
            <span class="font-medium text-gray-600">Voltage:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.voltage }}</span>
          </div>
          <div v-if="selectedSuggestionData?.cable_specs?.type">
            <span class="font-medium text-gray-600">Type:</span>
            <span class="ml-1">{{ selectedSuggestionData.cable_specs.type }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <template #footer>
      <div class="flex justify-between w-full">
        <Button 
          label="Upload New" 
          icon="pi pi-upload" 
          class="p-button-outlined"
          @click="handleUploadNew"
        />
        <div class="flex gap-2">
          <Button 
            label="Cancel" 
            class="p-button-text"
            @click="handleClose"
          />
          <Button 
            label="Use Selected" 
            icon="pi pi-check" 
            :disabled="selectedSuggestion === null"
            class="p-button-primary"
            @click="handleUseSelected"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    suggestions: {
        type: Array,
        default: () => []
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    assetId: {
        type: [String, Number],
        required: true
    }
});

const emit = defineEmits(['close', 'use-selected', 'upload-new', 'update:visible']);

const selectedSuggestion = ref(null);

// Computed property for selected suggestion data
const selectedSuggestionData = computed(() => {
    if (selectedSuggestion.value !== null && props.suggestions[selectedSuggestion.value]) {
        return props.suggestions[selectedSuggestion.value];
    }
    return null;
});

// Watch for visible changes to reset selection
watch(() => props.visible, (newVisible) => {
    if (newVisible) {
        selectedSuggestion.value = null;
    }
});

// Methods
const selectSuggestion = (index) => {
    selectedSuggestion.value = index;
};

const getSimilarityBadgeClass = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
};

const handleImageLoad = () => {
    // Image loaded successfully
};

const handleImageError = (event) => {
    // Handle image load error
    console.warn('Failed to load suggestion image:', event.target.src);
};

const handleClose = () => {
    emit('close');
};

const handleUseSelected = () => {
    if (selectedSuggestion.value !== null) {
        emit('use-selected', selectedSuggestionData.value);
    }
};

const handleUploadNew = () => {
    emit('upload-new');
};
</script>

<style scoped>
/* Custom styles for better visual hierarchy */
.group:hover .group-hover\:opacity-100 {
    opacity: 1;
}

/* Ensure proper aspect ratio for images */
.aspect-square {
    aspect-ratio: 1 / 1;
}

/* Smooth transitions for selection */
.transition-all {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
}
</style>
